import styled from '@emotion/styled';
import { settingsTheme as t } from '../../../theme';

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
  /** Color de relleno cuando la opción está activa (por defecto, el verde de marca). */
  activeColor?: string;
}

interface SegmentedControlProps<T extends string> {
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
}

const Row = styled.div({
  display: 'flex',
  gap: 10,
});

const Segment = styled.button<{ active: boolean; activeColor: string }>(({ active, activeColor }) => ({
  flex: 1,
  textAlign: 'center',
  padding: 10,
  borderRadius: t.radius.sm,
  cursor: 'pointer',
  fontFamily: t.font,
  fontSize: 14,
  fontWeight: active ? 600 : 500,
  background: active ? activeColor : t.color.surface,
  color: active ? t.color.surface : t.color.text,
  border: `1px solid ${active ? activeColor : t.color.inputBorder}`,
  transition: 'background 0.15s ease, border-color 0.15s ease, color 0.15s ease',
}));

/**
 * Primitivo de formulario: selector de opción única en segmentos de igual
 * ancho. Genérico sobre el valor; cada opción puede fijar su color activo
 * (verde para "Activo", gris para "Inactivo", etc.).
 */
function SegmentedControl<T extends string>({ options, value, onChange }: SegmentedControlProps<T>) {
  return (
    <Row>
      {options.map(option => (
        <Segment
          key={option.value}
          type="button"
          active={option.value === value}
          activeColor={option.activeColor || t.color.primary}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </Segment>
      ))}
    </Row>
  );
}

export default SegmentedControl;
